/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { Button, Icon, Modal, Notification } from '@scuf/common';
import React, { useContext, useState } from 'react';
import descriptors from '../../../../locales/en-US/descriptors';
import { useTranslation } from '@scuf/localization';
import { useFileUploadContext } from '../../../../context/FileUploadContext/FileUploadContext';
import FileUploadAction from '../../../../context/FileUploadContext/FileUploadActions';
import './PublishModalTheme.scss';
import { ApiContext } from '../../../../context/ApiContext';
import {
  POLLING_MILLI_SECONDS,
  PUBLISH_MODAL_REDIRECT_TIME
} from '../../../shared/Utils/Constants';

export interface IPublishMeterDataModalProps {
  onRefresh: () => void;
  isUploadInProgress?: boolean;
  filesToPublish?: number;
}

const PublishModal: React.FC<IPublishMeterDataModalProps> = (
  props: IPublishMeterDataModalProps
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const [state, dispatch] = useFileUploadContext();
  const { showPublishModal } = state;
  const [loading, setLoading] = useState(false);
  const { onPublishPointHistoryMeterData, notifyMessage } = useContext(
    ApiContext
  );

  const [redirectSeconds, setRedirectSeconds] = useState(
    PUBLISH_MODAL_REDIRECT_TIME
  );

  const closeModal = () => {
    dispatch(FileUploadAction.hidePublishModal(false));
  };

  const onSubmitClick = async () => {
    setLoading(true);
    try {
      onPublishPointHistoryMeterData &&
        (await onPublishPointHistoryMeterData());
    } catch (e) {
      console.error({ e });
      notifyMessage && notifyMessage(t(d.ERRORINPUBLISHING), 'error');
    } finally {
      props.onRefresh();
    }
  };

  const renderFooterButtons = () => {
    return (
      <div className="publish-modal-footer-buttons">
        <div>
          <Button
            className="no-button"
            type="secondary"
            onClick={() => {
              closeModal();
            }}
          >
            {t(d.NO)}
          </Button>
        </div>
        <div>
          <Button
            className="publish-button"
            style={{ marginLeft: 'auto' }}
            type="primary"
            onClick={onSubmitClick}
          >
            {t(d.PUBLISHBUTTON)}
          </Button>
        </div>
      </div>
    );
  };

  const renderConfirmPublishContent = () => {
    return props.isUploadInProgress ? (
      <>
        <Modal.Header className="publish-modal-header">
          <span>{t(d.PUBLISHMODALHEADER)}</span>
        </Modal.Header>
        <Modal.Content>
          <div className="publish-modal-content">
            <Notification hasIcon={true} severity="important">
              {t(d.WARNINGNOTIFICATIONFORPUBLISH)}
            </Notification>
            <div className="publish-modal-message">
              <p>
                {t(d.WARNINGINSTRUCTIONFORPUBLISHFILE, {
                  filesToPublish: props.filesToPublish || ''
                })}
              </p>
              <p>{t(d.NOTEFORPUBLISHFILE)}</p>
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer className="modal-footer-buttons">
          {renderFooterButtons()}
        </Modal.Footer>
      </>
    ) : (
      <>
        <Modal.Header className="publish-modal-header">
          <span>{t(d.PUBLISHMODALHEADER)}</span>
        </Modal.Header>
        <Modal.Content className="publish-modal-content">
          <div className="publish-modal-message">
            <p>{t(d.INSTRUCTIONFORPUBLISHFILE)}</p>
            <p>{t(d.NOTEFORPUBLISHFILE)}</p>
          </div>
        </Modal.Content>
        <Modal.Footer className="modal-footer-buttons">
          {renderFooterButtons()}
        </Modal.Footer>
      </>
    );
  };

  const startTimer = () => {
    const reduceCountTime = POLLING_MILLI_SECONDS;
    let timer = setTimeout(() => {
      if (redirectSeconds === 0) {
        setRedirectSeconds(PUBLISH_MODAL_REDIRECT_TIME);
        clearTimeout(timer);
        setLoading(false);
        closeModal();
      } else {
        setRedirectSeconds(redirectSeconds - 1);
      }
    }, reduceCountTime);
  };

  const renderLoadingContent = () => {
    startTimer();
    return (
      <Modal.Content>
        <div className="loading-status">
          <div>
            <div className="align-center">
              <Button
                className="publish-modal-loading"
                loading={true}
                disabled={true}
                content="Loading"
              />
            </div>
            <div className="align-center f-20 mw-loading-title text-center">
              {t(d.METERDATAPUBLISHINGMSG)}
            </div>
            <hr className="hr-width mtb-20" />
            <div className="align-center flex-center redirect-time-msg">
              <Icon name="time" root="common" /> {t(d.REDIRECTMESSAGE1)}{' '}
              {redirectSeconds} {t(d.REDIRECTMESSAGE2)}
            </div>
          </div>
        </div>
      </Modal.Content>
    );
  };

  const renderPublishModalContent = () => {
    if (loading) return renderLoadingContent();
    return renderConfirmPublishContent();
  };

  return (
    <React.Fragment>
      <Modal
        className="publish-modal"
        size={!loading ? 'small' : 'large'}
        onClose={closeModal}
        open={showPublishModal}
        closeOnDimmerClick={false}
        closeIcon={true && !loading}
      >
        {renderPublishModalContent()}
      </Modal>
    </React.Fragment>
  );
};

export default PublishModal;
